import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useToast } from '../hooks/useToast';
import { WAG_COLORS } from '../hooks/useSchedulerData';
import {
  ArrowLeft,
  UserPlus,
  Edit2,
  Trash2,
  RotateCcw,
  Shield,
  HardHat,
  Zap,
  Briefcase,
  GraduationCap,
  Save,
  X,
  Building2,
  MessageSquare,
  LayoutList,
  Archive,
  Users,
  Euro,
  KeyRound,
  Copy,
  Check,
  Mail,
  Clock
} from 'lucide-react';

// --- TYPES ---
type CompanyType = 'oger' | 'wag';

interface Employee {
  id: string;
  full_name: string;
  role: string;
  is_interim: boolean;
  pin_hash: string;
  is_active: boolean;
  manager_id: string | null;
  phone?: string;
  email?: string;
  interim_agency?: string;
  comments?: string;
  company?: CompanyType; // 'oger' par défaut si null/undefined
  custom_weekly_hours?: number | null;
  hourly_rate?: number | null; // Taux horaire €/h (non renseigné = null)
  auth_user_id?: string | null;
}

// Constantes pour les sociétés
const COMPANIES = [
  { value: 'oger', label: 'Oger Energies' },
  { value: 'wag', label: 'WAG' }
];


const ROLES = [
  { value: 'admin', label: 'Administrateur' },
  { value: 'chef_chantier', label: 'Chef de Chantier' },
  { value: 'chef_equipe', label: 'Chef d\'Équipe' },
  { value: 'electricien', label: 'Monteur Électricien' },
  { value: 'bureau_etude', label: 'Bureau d\'Étude' },
  { value: 'apprenti_be', label: 'Apprenti BE' },
  { value: 'secretariat', label: 'Secrétariat' }
];

// Rôles nécessitant un mot de passe renforcé (au lieu d'un simple PIN)
const STRONG_PASSWORD_ROLES = ['admin', 'bureau_etude', 'secretariat'];

// Validation du mot de passe renforcé
const validateStrongPassword = (password: string): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (password.length < 8) {
    errors.push('Au moins 8 caractères');
  }
  if (!/[a-z]/.test(password)) {
    errors.push('Au moins une lettre minuscule');
  }
  if (!/[A-Z]/.test(password)) {
    errors.push('Au moins une lettre majuscule');
  }
  if (!/[0-9]/.test(password)) {
    errors.push('Au moins un chiffre');
  }
  if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
    errors.push('Au moins un caractère spécial (!@#$%^&*...)');
  }

  return { isValid: errors.length === 0, errors };
};

// Vérifier si un rôle nécessite un mot de passe renforcé
const requiresStrongPassword = (role: string): boolean => STRONG_PASSWORD_ROLES.includes(role);

const AGENCIES = [
  'Adecco', 
  'Randstad', 
  'Manpower', 
  'Proman', 
  'Synergie', 
  'Crit', 
  'Samsic'
];

export default function AdminEmployees() {
  const navigate = useNavigate();
  const { toast } = useToast();

  // États
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'active' | 'archived'>('active'); // NOUVEAU: Filtre actifs/archivés
  
  // Reset mot de passe
  const [resetLoading, setResetLoading] = useState(false);
  const [temporaryPassword, setTemporaryPassword] = useState<string | null>(null);
  const [copiedPassword, setCopiedPassword] = useState(false);

  // Formulaire
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    full_name: '',
    role: 'electricien',
    is_interim: false,
    interim_agency: '',
    custom_weekly_hours: '' as string,
    pin_hash: '',
    manager_id: '',
    phone: '',
    email: '',
    comments: '',
    company: 'oger' as CompanyType,
    hourly_rate: '' as string // Taux horaire en €/h (chaîne pour le input)
  });

  // --- CHARGEMENT ---
  const loadEmployees = useCallback(async () => {
    try {
      // Sélectionner tous les champs SAUF pin_hash (sécurité)
      const { data, error } = await supabase
        .from('employees')
        .select('id, created_at, full_name, short_name, role, is_interim, is_active, manager_id, phone, email, interim_agency, comments, company, custom_weekly_hours, hourly_rate, auth_user_id')
        .order('is_active', { ascending: false })
        .order('full_name');
      
      if (error) throw error;
      
      if (data) {
        // Cast via unknown car pin_hash n'est pas récupéré pour des raisons de sécurité
        setEmployees(data as unknown as Employee[]);
      }
    } catch (error) {
      console.error("Erreur chargement:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  // --- ACTIONS ---
  const handleEdit = (emp: Employee) => {
    setEditingId(emp.id);
    setFormData({
      full_name: emp.full_name,
      role: emp.role,
      is_interim: emp.is_interim,
      interim_agency: emp.interim_agency || '',
      custom_weekly_hours: emp.custom_weekly_hours ? String(emp.custom_weekly_hours) : '',
      pin_hash: '', // Vide en édition : laisser vide = pas de changement
      manager_id: emp.manager_id || '',
      phone: emp.phone || '',
      email: emp.email || '',
      comments: emp.comments || '',
      company: emp.company || 'oger',
      hourly_rate: emp.hourly_rate != null ? String(emp.hourly_rate) : ''
    });
    setTemporaryPassword(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleReset = () => {
    setEditingId(null);
    setFormData({
      full_name: '',
      role: 'electricien',
      is_interim: false,
      interim_agency: '',
      custom_weekly_hours: '',
      pin_hash: '',
      manager_id: '',
      phone: '',
      email: '',
      comments: '',
      company: 'oger',
      hourly_rate: ''
    });
  };

  // Appeler l'Edge Function manage-auth
  const callManageAuth = async (body: Record<string, unknown>) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Non authentifié');

    const res = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-auth`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
        },
        body: JSON.stringify(body)
      }
    );

    const result = await res.json();
    if (!res.ok) throw new Error(result.error || 'Erreur Edge Function');
    return result;
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    // Validation du nom
    if (!formData.full_name) { toast.warning("Le nom est obligatoire."); return; }

    const isCreating = !editingId;

    // Validation PIN/Mot de passe selon le rôle (non requis pour intérimaires)
    if (!formData.is_interim) {
      // En création : mot de passe obligatoire ; en édition : optionnel (vide = pas de changement)
      if (isCreating && !formData.pin_hash) {
        toast.warning(requiresStrongPassword(formData.role)
          ? "Le mot de passe est obligatoire pour ce rôle."
          : "Le code PIN est obligatoire pour les collaborateurs.");
        return;
      }

      // Valider seulement si un mot de passe est saisi
      if (formData.pin_hash) {
        if (requiresStrongPassword(formData.role)) {
          const validation = validateStrongPassword(formData.pin_hash);
          if (!validation.isValid) {
            toast.warning(`Mot de passe invalide : ${validation.errors.join(', ')}`);
            return;
          }
        } else {
          if (!/^\d{6}$/.test(formData.pin_hash)) {
            toast.warning("Le code PIN doit contenir exactement 6 chiffres.");
            return;
          }
        }
      }
    }

    setLoading(true);
    try {
      // WAG = permanents uniquement (pas d'intérimaires)
      const isWag = formData.company === 'wag';
      const isInterim = isWag ? false : formData.is_interim;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const payload: Record<string, any> = {
        full_name: formData.full_name,
        role: formData.role,
        is_interim: isInterim,
        interim_agency: isInterim ? formData.interim_agency : null,
        custom_weekly_hours: formData.custom_weekly_hours
          ? parseFloat(formData.custom_weekly_hours)
          : null,
        manager_id: formData.manager_id || null,
        phone: formData.phone || null,
        email: formData.email || null,
        comments: formData.comments || null,
        company: formData.company || 'oger',
        hourly_rate: formData.role === 'secretariat'
          ? null
          : formData.hourly_rate !== '' ? parseFloat(formData.hourly_rate) : null
      };

      // Inclure pin_hash dans le payload seulement si renseigné
      if (formData.pin_hash) {
        payload.pin_hash = formData.pin_hash;
      }

      if (editingId) {
        // ── MODIFICATION ──
        const { error } = await supabase.from('employees').update(payload).eq('id', editingId);
        if (error) throw error;

        // Synchroniser le mot de passe dans Supabase Auth si modifié (non-intérimaire)
        if (formData.pin_hash && !isInterim) {
          const editedEmp = employees.find(e => e.id === editingId);
          try {
            if (editedEmp?.auth_user_id) {
              // Compte auth existant → mettre à jour le mot de passe
              await callManageAuth({
                action: 'update-password',
                employee_id: editingId,
                password: formData.pin_hash
              });
            } else {
              // Pas de compte auth → le créer maintenant
              await callManageAuth({
                action: 'create-user',
                employee_id: editingId,
                password: formData.pin_hash,
                full_name: formData.full_name,
                role: formData.role
              });
            }
          } catch (authErr) {
            console.error('Erreur synchro auth:', authErr);
            toast.warning(`Employé mis à jour, mais erreur de synchronisation du mot de passe auth : ${authErr instanceof Error ? authErr.message : 'Erreur inconnue'}`);
          }
        }
      } else {
        // ── CRÉATION ──
        // is_active n'appartient qu'à la création. Le laisser dans le payload
        // partagé réactivait silencieusement tout employé archivé dès qu'on
        // éditait sa fiche — corriger un numéro de téléphone le remettait en
        // circulation sans que personne ne le voie.
        payload.is_active = true;

        // Ajouter pin_hash au payload pour la création
        if (!isInterim) {
          payload.pin_hash = formData.pin_hash;
        }

        const { data: insertedData, error } = await supabase
          .from('employees')
          .insert(payload)
          .select('id')
          .single();
        if (error) throw error;

        // Créer le compte Supabase Auth pour les non-intérimaires
        if (!isInterim && insertedData) {
          try {
            await callManageAuth({
              action: 'create-user',
              employee_id: insertedData.id,
              password: formData.pin_hash,
              full_name: formData.full_name,
              role: formData.role
            });
          } catch (authErr) {
            // Rollback : supprimer l'employé créé
            await supabase.from('employees').delete().eq('id', insertedData.id);
            throw new Error(`Erreur création compte auth : ${authErr instanceof Error ? authErr.message : 'Erreur inconnue'}`);
          }
        }
      }

      handleReset();
      setTemporaryPassword(null);
      await loadEmployees();

    } catch (error) {
      const msg = error instanceof Error ? error.message : "Erreur inconnue";
      toast.error("Erreur : " + msg);
    } finally {
      setLoading(false);
    }
  };

  // Réinitialiser le mot de passe d'un employé
  const handleResetPassword = async (emp: Employee) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Réinitialiser le mot de passe de "${emp.full_name}" ?\n\nUn mot de passe temporaire sera généré.`)) return;

    setResetLoading(true);
    try {
      const result = await callManageAuth({
        action: 'reset-password',
        employee_id: emp.id
      });

      setTemporaryPassword(result.temporary_password);
      setCopiedPassword(false);
      toast.success(`Mot de passe réinitialisé pour ${emp.full_name}. Temporaire : ${result.temporary_password}`);
      await loadEmployees();
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Erreur inconnue";
      toast.error("Erreur : " + msg);
    } finally {
      setResetLoading(false);
    }
  };

  const toggleActive = async (id: string, currentState: boolean) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(currentState ? "Désactiver cet employé ?" : "Réactiver cet employé ?")) return;

    setLoading(true);
    try {
      // Le résultat doit être lu : la base refuse toute désactivation laissant
      // Centralis sans administrateur joignable (trigger employees_keep_one_admin,
      // migration 20260810a). Sans cette lecture, le refus serait invisible et
      // l'écran se rechargerait comme si l'opération avait réussi.
      const { error } = await supabase
        .from('employees')
        .update({ is_active: !currentState })
        .eq('id', id);

      if (error) throw error;
      await loadEmployees();
    } catch (error) {
      console.error(error);
      const msg = error instanceof Error ? error.message : "Erreur inconnue";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (emp: Employee) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`⚠️ ATTENTION : Supprimer définitivement l'employé "${emp.full_name}" ?\n\nCette action est IRRÉVERSIBLE et supprimera toutes les données associées.`)) return;

    setLoading(true);
    try {
      const { error } = await supabase.from('employees').delete().eq('id', emp.id);
      if (error) throw error;
      await loadEmployees();
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la suppression. L'employé est peut-être lié à des données existantes.");
    } finally {
      setLoading(false);
    }
  };

  // Helper pour l'icône de rôle
  const getRoleIcon = (role: string) => {
    switch(role) {
      case 'admin': return <Shield size={16} color="#6366f1" />;
      case 'chef_chantier': return <HardHat size={16} color="#f59e0b" />;
      case 'chef_equipe': return <Users size={16} color="#0d9488" />;
      case 'electricien': return <Zap size={16} color="#10b981" />;
      case 'bureau_etude': return <Briefcase size={16} color="#0ea5e9" />;
      case 'apprenti_be': return <GraduationCap size={16} color="#0ea5e9" />;
      default: return <Zap size={16} />;
    }
  };

  // Filtrer les employés selon le mode de vue
  const filteredEmployees = employees.filter(e => viewMode === 'active' ? e.is_active : !e.is_active);

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', paddingBottom: '80px', background: '#f8fafc', minHeight: '100vh' }}>

      {/* HEADER MODERNE */}
      <div style={{
        background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
        padding: '24px 30px',
        marginBottom: '30px',
        borderRadius: '0 0 24px 24px',
        boxShadow: '0 4px 20px rgba(139,92,246,0.2)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={() => navigate('/')}
            style={{
              background: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '12px',
              padding: '12px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <ArrowLeft size={20} color="white" />
          </button>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.6rem', color: 'white', fontWeight: '700' }}>Gestion du Personnel</h1>
            <p style={{ margin: '4px 0 0 0', fontSize: '0.9rem', color: 'rgba(255,255,255,0.7)' }}>
              Collaborateurs et intérimaires
            </p>
          </div>
        </div>

        {/* TOGGLE ACTIFS / ARCHIVÉS */}
        <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
          <button
            onClick={() => setViewMode('active')}
            style={{
              padding: '12px 24px',
              borderRadius: '12px',
              background: viewMode === 'active' ? 'white' : 'rgba(255,255,255,0.1)',
              color: viewMode === 'active' ? '#8b5cf6' : 'rgba(255,255,255,0.8)',
              cursor: 'pointer',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              boxShadow: viewMode === 'active' ? '0 4px 12px rgba(0,0,0,0.15)' : 'none',
              border: viewMode === 'active' ? 'none' : '1px solid rgba(255,255,255,0.2)',
              fontSize: '0.95rem',
              transition: 'all 0.25s ease'
            }}
          >
            <LayoutList size={18} /> Employés Actifs
            <span style={{
              background: viewMode === 'active' ? '#8b5cf6' : 'rgba(255,255,255,0.2)',
              color: 'white',
              padding: '2px 10px',
              borderRadius: '20px',
              fontSize: '0.8rem',
              fontWeight: '700'
            }}>
              {employees.filter(e => e.is_active).length}
            </span>
          </button>
          <button
            onClick={() => setViewMode('archived')}
            style={{
              padding: '12px 24px',
              borderRadius: '12px',
              background: viewMode === 'archived' ? 'white' : 'rgba(255,255,255,0.1)',
              color: viewMode === 'archived' ? '#64748b' : 'rgba(255,255,255,0.8)',
              cursor: 'pointer',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              boxShadow: viewMode === 'archived' ? '0 4px 12px rgba(0,0,0,0.15)' : 'none',
              border: viewMode === 'archived' ? 'none' : '1px solid rgba(255,255,255,0.2)',
              fontSize: '0.95rem',
              transition: 'all 0.25s ease'
            }}
          >
            <Archive size={18} /> Employés Archivés
            <span style={{
              background: viewMode === 'archived' ? '#64748b' : 'rgba(255,255,255,0.2)',
              color: 'white',
              padding: '2px 10px',
              borderRadius: '20px',
              fontSize: '0.8rem',
              fontWeight: '700'
            }}>
              {employees.filter(e => !e.is_active).length}
            </span>
          </button>
        </div>
      </div>

      {/* CONTENU PRINCIPAL */}
      <div style={{ padding: '0 20px' }}>

      {/* FORMULAIRE */}
      <div style={{ background: 'white', padding: '24px', borderRadius: '16px', border: '1px solid #e2e8f0', marginBottom: '30px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
        <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#334155', display:'flex', alignItems:'center', gap:'10px', fontSize:'1.1rem' }}>
          <div style={{ background: editingId ? '#eff6ff' : '#ecfdf5', padding:'8px', borderRadius:'8px', color: editingId ? '#2563eb' : '#10b981' }}>
            {editingId ? <Edit2 size={20} /> : <UserPlus size={20} />}
          </div>
          {editingId ? 'Modifier un collaborateur' : 'Ajouter un collaborateur'}
        </h3>
        
        <form onSubmit={handleSave} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px' }}>
          
          <label style={{fontWeight:'600', color:'#475569', fontSize:'0.9rem'}}>Nom Complet
            <input type="text" required value={formData.full_name} onChange={e => setFormData({...formData, full_name: e.target.value})} style={{ width: '100%', padding: '12px', marginTop:'6px', border:'1px solid #cbd5e1', borderRadius:'8px', fontSize:'1rem' }} placeholder="Ex: Jean Dupont" />
          </label>

          {/* Code PIN ou Mot de passe selon le rôle */}
          {!formData.is_interim && (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <label style={{fontWeight:'600', color:'#475569', fontSize:'0.9rem'}}>
                {requiresStrongPassword(formData.role) ? 'Mot de passe' : 'Code PIN (6 chiffres)'}
                {editingId && <span style={{ fontWeight: '400', color: '#94a3b8', fontSize: '0.8rem' }}> — laisser vide pour ne pas modifier</span>}
                <input
                  type={requiresStrongPassword(formData.role) ? 'password' : 'text'}
                  required={!editingId}
                  value={formData.pin_hash}
                  onChange={e => {
                    if (requiresStrongPassword(formData.role)) {
                      setFormData({...formData, pin_hash: e.target.value});
                    } else {
                      setFormData({...formData, pin_hash: e.target.value.replace(/\D/g, '').slice(0, 6)});
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '12px',
                    marginTop:'6px',
                    border: `1px solid ${requiresStrongPassword(formData.role) ? '#7c3aed' : '#cbd5e1'}`,
                    borderRadius:'8px',
                    fontSize:'1rem',
                    background: requiresStrongPassword(formData.role) ? '#faf5ff' : 'white'
                  }}
                  placeholder={editingId
                    ? (requiresStrongPassword(formData.role) ? 'Nouveau mot de passe (optionnel)' : 'Nouveau PIN (optionnel)')
                    : (requiresStrongPassword(formData.role) ? 'Min. 8 car., maj., min., chiffre, spécial' : 'Ex: 123456')
                  }
                  maxLength={requiresStrongPassword(formData.role) ? 50 : 6}
                  inputMode={requiresStrongPassword(formData.role) ? 'text' : 'numeric'}
                />
              </label>
              {requiresStrongPassword(formData.role) && (
                <div style={{ marginTop: '8px', fontSize: '0.75rem', color: '#6b7280', background: '#f3e8ff', padding: '8px 12px', borderRadius: '6px' }}>
                  <strong style={{ color: '#7c3aed' }}>Exigences :</strong> 8+ caractères, majuscule, minuscule, chiffre, caractère spécial
                </div>
              )}
            </div>
          )}

          <label style={{fontWeight:'600', color:'#475569', fontSize:'0.9rem'}}>Téléphone
            <input
              type="tel"
              value={formData.phone}
              onChange={e => setFormData({...formData, phone: e.target.value})}
              style={{ width: '100%', padding: '12px', marginTop:'6px', border:'1px solid #cbd5e1', borderRadius:'8px', fontSize:'1rem' }}
              placeholder="Ex: +33612345678"
            />
          </label>

          <label style={{fontWeight:'600', color:'#475569', fontSize:'0.9rem'}}>Email
            <input
              type="email"
              value={formData.email}
              onChange={e => setFormData({...formData, email: e.target.value})}
              style={{ width: '100%', padding: '12px', marginTop:'6px', border:'1px solid #cbd5e1', borderRadius:'8px', fontSize:'1rem' }}
              placeholder="prenom.nom@email.com"
            />
          </label>

          <label style={{fontWeight:'600', color:'#475569', fontSize:'0.9rem'}}>Rôle
            <select value={formData.role} onChange={e => setFormData({...formData, role: e.target.value})} style={{ width: '100%', padding: '12px', marginTop:'6px', border:'1px solid #cbd5e1', borderRadius:'8px', background:'white', fontSize:'1rem' }}>
              {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </label>

          {/* Sélecteur Société */}
          <label style={{fontWeight:'600', color:'#475569', fontSize:'0.9rem'}}>Société
            <select
              value={formData.company}
              onChange={e => {
                const newCompany = e.target.value as CompanyType;
                setFormData({
                  ...formData,
                  company: newCompany,
                  // WAG = pas d'intérimaires
                  is_interim: newCompany === 'wag' ? false : formData.is_interim
                });
              }}
              style={{
                width: '100%',
                padding: '12px',
                marginTop:'6px',
                border: formData.company === 'wag' ? `2px solid ${WAG_COLORS.border}` : '1px solid #cbd5e1',
                borderRadius:'8px',
                background: formData.company === 'wag' ? WAG_COLORS.bg : 'white',
                fontSize:'1rem',
                fontWeight: formData.company === 'wag' ? '600' : '400',
                color: formData.company === 'wag' ? WAG_COLORS.text : 'inherit'
              }}
            >
              {COMPANIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </label>

          {/* Checkbox Intérimaire (masqué pour WAG) */}
          {formData.company !== 'wag' && (
          <div style={{ display: 'flex', flexDirection:'column', gap:'15px', marginTop: '10px', gridColumn: '1 / -1', background:'#f8fafc', padding:'15px', borderRadius:'12px', border:'1px solid #e2e8f0' }}>

             <div style={{ display: 'flex', alignItems: 'center' }}>
                <input type="checkbox" id="interim" checked={formData.is_interim} onChange={e => setFormData({...formData, is_interim: e.target.checked})} style={{ width:'20px', height:'20px', marginRight: '10px', cursor:'pointer', accentColor:'#f59e0b' }} />
                <label htmlFor="interim" style={{cursor:'pointer', fontWeight:'700', color:'#475569', fontSize:'0.95rem'}}>Ce collaborateur est Intérimaire</label>
             </div>

             {/* SECTION INTÉRIMAIRE (Apparaît si coché) */}
             {formData.is_interim && (
                <div style={{ display:'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap:'20px', paddingLeft:'30px' }}>

                    {/* Saisie Agence avec Datalist */}
                    <label style={{fontWeight:'600', color:'#d97706', fontSize:'0.9rem'}}>Agence d'Intérim :
                        <div style={{ position: 'relative' }}>
                            <Building2 size={16} color="#d97706" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                            <input
                                list="agencies-list"
                                type="text"
                                value={formData.interim_agency}
                                onChange={e => setFormData({...formData, interim_agency: e.target.value})}
                                placeholder="Ex: Adecco, Manpower..."
                                style={{ width: '100%', padding: '12px 12px 12px 36px', marginTop:'6px', border:'1px solid #f59e0b', borderRadius:'8px', fontSize:'1rem' }}
                            />
                            <datalist id="agencies-list">
                                {AGENCIES.map(agency => (
                                    <option key={agency} value={agency} />
                                ))}
                            </datalist>
                        </div>
                    </label>
                </div>
             )}

          </div>
          )}

          {/* SECTION BASE HORAIRE — visible pour TOUS les employés */}
          <div style={{ gridColumn: '1 / -1', marginTop: '10px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600', color: '#475569', fontSize: '0.9rem', marginBottom: '8px' }}>
              <Clock size={16} color="#6366f1" /> Base horaire hebdomadaire
            </label>
            <select
              value={formData.custom_weekly_hours}
              onChange={e => setFormData({...formData, custom_weekly_hours: e.target.value})}
              style={{ width: '100%', maxWidth: '300px', padding: '12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '1rem', background: 'white' }}
            >
              <option value="">Standard (32h/38h alternées)</option>
              <option value="35">35h</option>
              <option value="37">37h</option>
              <option value="38">38h</option>
              <option value="39">39h</option>
              <option value="40">40h</option>
            </select>
            <p style={{ margin: '6px 0 0', fontSize: '0.8rem', color: '#94a3b8' }}>
              Par défaut : semaines alternées 32h (impaire) / 38h (paire)
            </p>
          </div>

          {/* SECTION TAUX HORAIRE (masqué pour secrétariat) */}
          {formData.role !== 'secretariat' && (
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight:'600', color:'#475569', fontSize:'0.9rem', marginBottom: '6px' }}>
                <Euro size={15} color="#475569" /> Taux horaire (€/h)
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={formData.hourly_rate}
                  onChange={e => setFormData({...formData, hourly_rate: e.target.value})}
                  placeholder="Ex: 42.50"
                  style={{
                    width: '140px',
                    padding: '12px',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    background: 'white'
                  }}
                />
                <span style={{ color:'#94a3b8', fontSize:'0.82rem', whiteSpace: 'nowrap' }}>€/h — coût MO reporting</span>
              </div>
            </div>
          )}

          {/* SECTION COMMENTAIRES */}
          <label style={{ gridColumn: '1 / -1', fontWeight:'600', color:'#475569', fontSize:'0.9rem' }}>
            Commentaires / Notes (Interne)
            <textarea 
                value={formData.comments} 
                onChange={e => setFormData({...formData, comments: e.target.value})} 
                placeholder="Informations supplémentaires, restrictions médicales, notes RH..."
                style={{ width: '100%', padding: '12px', marginTop:'6px', border:'1px solid #cbd5e1', borderRadius:'8px', fontSize:'0.95rem', minHeight:'80px', fontFamily:'Inter, sans-serif' }} 
            />
          </label>

          <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '10px', marginTop: '10px', borderTop:'1px solid #e2e8f0', paddingTop:'20px' }}>
            {editingId && (
              <button type="button" onClick={handleReset} style={{ padding: '12px 20px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '8px', cursor: 'pointer', fontWeight:'600', color:'#475569', display:'flex', alignItems:'center', gap:'8px' }}>
                <X size={18} /> Annuler
              </button>
            )}
            <button type="submit" disabled={loading} style={{ padding: '12px 25px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', cursor: loading ? 'wait' : 'pointer', fontWeight: '600', boxShadow:'0 2px 4px rgba(37,99,235,0.2)', display:'flex', alignItems:'center', gap:'8px' }}>
              <Save size={18} /> {loading ? 'Traitement...' : (editingId ? 'Enregistrer' : 'Créer le compte')}
            </button>
          </div>
        </form>
      </div>

      {/* ENCADRÉ MOT DE PASSE TEMPORAIRE */}
      {temporaryPassword && (
        <div style={{
          background: 'linear-gradient(135deg, #faf5ff, #ede9fe)',
          border: '2px solid #7c3aed',
          borderRadius: '16px',
          padding: '20px 24px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          flexWrap: 'wrap'
        }}>
          <KeyRound size={24} color="#7c3aed" />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: '700', color: '#4c1d95', fontSize: '0.95rem', marginBottom: '4px' }}>
              Mot de passe temporaire généré
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <code style={{
                background: 'white',
                padding: '10px 16px',
                borderRadius: '8px',
                fontFamily: 'monospace',
                fontSize: '1.2rem',
                fontWeight: '700',
                color: '#1e293b',
                letterSpacing: '2px',
                border: '1px solid #c4b5fd'
              }}>
                {temporaryPassword}
              </code>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(temporaryPassword);
                  setCopiedPassword(true);
                  setTimeout(() => setCopiedPassword(false), 2000);
                }}
                style={{
                  background: copiedPassword ? '#10b981' : '#7c3aed',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '0.85rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s'
                }}
              >
                {copiedPassword ? <><Check size={14} /> Copié !</> : <><Copy size={14} /> Copier</>}
              </button>
            </div>
            <div style={{ marginTop: '8px', fontSize: '0.8rem', color: '#6d28d9' }}>
              Communiquez ce mot de passe à l'employé. Il pourra l'utiliser pour se connecter.
            </div>
          </div>
          <button
            onClick={() => setTemporaryPassword(null)}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: '#7c3aed',
              padding: '4px'
            }}
          >
            <X size={20} />
          </button>
        </div>
      )}

      {/* TABLEAU */}
      {loading && employees.length === 0 ? (
        <p style={{textAlign:'center', color:'#64748b', padding:'40px'}}>Chargement...</p>
      ) : filteredEmployees.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '80px 40px',
          background: 'linear-gradient(145deg, #ffffff, #f8fafc)',
          borderRadius: '24px',
          border: '2px dashed #e2e8f0',
          color: '#94a3b8'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            background: 'linear-gradient(135deg, #f1f5f9, #e2e8f0)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px'
          }}>
            <Users size={36} color="#94a3b8" />
          </div>
          <p style={{ fontSize: '1.1rem', fontWeight: '500', margin: 0 }}>
            {viewMode === 'active' ? 'Aucun employé actif' : 'Aucun employé archivé'}
          </p>
          <p style={{ fontSize: '0.9rem', marginTop: '8px', color: '#cbd5e1' }}>
            {viewMode === 'active' ? 'Ajoutez un collaborateur ci-dessus' : 'Les employés archivés apparaîtront ici'}
          </p>
        </div>
      ) : (
        <div style={{
          overflowX: 'auto',
          background: 'white',
          borderRadius: '20px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05), 0 4px 6px -2px rgba(0,0,0,0.03)'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', borderBottom: '1px solid #e2e8f0' }}>
              <tr>
                <th style={{ padding: '18px 16px', textAlign: 'left', fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '700', letterSpacing: '0.05em' }}>Collaborateur</th>
                <th style={{ padding: '18px 16px', textAlign: 'left', fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '700', letterSpacing: '0.05em' }}>Rôle</th>
                <th style={{ padding: '18px 16px', textAlign: 'right', fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '700', letterSpacing: '0.05em' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.map((emp, index) => (
                <tr
                  key={emp.id}
                  style={{
                    borderBottom: index < filteredEmployees.length - 1 ? '1px solid #f1f5f9' : 'none',
                    background: 'white',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => { e.currentTarget.style.background = '#fafbfc'; }}
                  onMouseOut={(e) => { e.currentTarget.style.background = 'white'; }}
                >
                  <td style={{ padding: '18px 16px', color: '#1e293b' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      {/* Avatar avec couleur selon société/statut */}
                      <div style={{
                        width: '44px',
                        height: '44px',
                        background: emp.company === 'wag'
                          ? WAG_COLORS.bgGradient
                          : emp.is_interim
                            ? 'linear-gradient(135deg, #fffbeb, #fef3c7)'
                            : 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: emp.company === 'wag'
                          ? `2px solid ${WAG_COLORS.border}`
                          : emp.is_interim
                            ? '1px solid #fcd34d'
                            : '1px solid #e2e8f0'
                      }}>
                        {getRoleIcon(emp.role)}
                      </div>
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {emp.full_name}
                          {emp.comments && (
                            <span title="Commentaire présent" style={{ display: 'flex' }}>
                              <MessageSquare size={14} color="#3b82f6" />
                            </span>
                          )}
                        </div>
                        {/* Badge WAG */}
                        {emp.company === 'wag' && (
                          <div style={{ marginTop: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{
                              fontSize: '0.7rem',
                              background: WAG_COLORS.bgGradient,
                              color: WAG_COLORS.text,
                              padding: '3px 10px',
                              borderRadius: '20px',
                              fontWeight: '700',
                              border: `1px solid ${WAG_COLORS.border}`
                            }}>
                              WAG
                            </span>
                          </div>
                        )}
                        {/* Badge Intérim (pour Oger uniquement) */}
                        {emp.is_interim && emp.company !== 'wag' && (
                          <div style={{ marginTop: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{
                              fontSize: '0.7rem',
                              background: 'linear-gradient(135deg, #fffbeb, #fef3c7)',
                              color: '#b45309',
                              padding: '3px 10px',
                              borderRadius: '20px',
                              fontWeight: '700',
                              border: '1px solid #fcd34d'
                            }}>
                              INTÉRIM
                            </span>
                            {emp.interim_agency && (
                              <span style={{ fontSize: '0.85rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Building2 size={12} /> {emp.interim_agency}
                              </span>
                            )}
                          </div>
                        )}
                        {/* Email */}
                        {emp.email && (
                          <div style={{ marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem', color: '#64748b' }}>
                            <Mail size={12} /> {emp.email}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '18px 16px', color: '#475569' }}>
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      background: '#f8fafc',
                      padding: '8px 14px',
                      borderRadius: '10px',
                      fontWeight: '500'
                    }}>
                      {getRoleIcon(emp.role)}
                      <span>{ROLES.find(r => r.value === emp.role)?.label || emp.role}</span>
                    </div>
                  </td>
                  <td style={{ padding: '18px 16px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => handleEdit(emp)}
                        title="Modifier"
                        style={{
                          background: 'linear-gradient(145deg, #ffffff, #eff6ff)',
                          border: '1px solid #bfdbfe',
                          borderRadius: '10px',
                          padding: '10px',
                          cursor: 'pointer',
                          color: '#3b82f6',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Edit2 size={16} />
                      </button>
                      {/* Bouton Réinitialiser mot de passe (non-intérimaires avec compte auth) */}
                      {!emp.is_interim && emp.auth_user_id && (
                        <button
                          onClick={() => handleResetPassword(emp)}
                          disabled={resetLoading}
                          title="Réinitialiser le mot de passe"
                          style={{
                            background: 'linear-gradient(145deg, #ffffff, #faf5ff)',
                            border: '1px solid #d8b4fe',
                            borderRadius: '10px',
                            padding: '10px',
                            cursor: resetLoading ? 'wait' : 'pointer',
                            color: '#7c3aed',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: resetLoading ? 0.6 : 1
                          }}
                        >
                          <KeyRound size={16} />
                        </button>
                      )}
                      <button
                        onClick={() => toggleActive(emp.id, emp.is_active)}
                        title={emp.is_active ? "Archiver" : "Réactiver"}
                        style={{
                          background: emp.is_active
                            ? 'linear-gradient(145deg, #ffffff, #f8fafc)'
                            : 'linear-gradient(145deg, #ecfdf5, #d1fae5)',
                          border: emp.is_active ? '1px solid #e2e8f0' : '1px solid #86efac',
                          borderRadius: '10px',
                          padding: '10px',
                          cursor: 'pointer',
                          color: emp.is_active ? '#64748b' : '#10b981',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {emp.is_active ? <Archive size={16} /> : <RotateCcw size={16} />}
                      </button>
                      {/* Bouton suppression définitive (visible uniquement pour les archivés) */}
                      {!emp.is_active && (
                        <button
                          onClick={() => handleDelete(emp)}
                          title="Supprimer définitivement"
                          style={{
                            background: 'linear-gradient(145deg, #fef2f2, #fee2e2)',
                            border: '1px solid #fecaca',
                            borderRadius: '10px',
                            padding: '10px',
                            cursor: 'pointer',
                            color: '#dc2626',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </div>
    </div>
  );
}